import Icon from 'core/modules/atoms/Icon';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ChatAudioRecorder.scss';

function ChatAudioRecorder({ onCancel, onStop }) {
  const { t } = useTranslation(['chatbot']);
  const wavesRef = useRef(null);
  const [isLive, setIsLive] = useState(false);
  const rafRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const sourceRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    async function setupAnalyser() {
      try {
        // Request mic stream (separate from Web Speech API)
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false,
        });
        if (!isMounted) {
          // If component unmounted meanwhile, stop tracks
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        mediaStreamRef.current = stream;

        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        const source = audioContext.createMediaStreamSource(stream);
        sourceRef.current = source;

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 512; // frequencyBinCount = 256
        analyser.smoothingTimeConstant = 0.85;
        analyserRef.current = analyser;

        source.connect(analyser);

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const barsCount = 24;
        const halfCount = Math.floor(barsCount / 2);
        const minHeight = 4; // px
        const maxHeight = 30; // px
        const range = maxHeight - minHeight;

        const updateBars = () => {
          if (!wavesRef.current) return;
          analyser.getByteFrequencyData(dataArray);

          // Dividir el espectro en halfCount buckets y reflejar desde el centro
          const bucketSize = Math.floor(bufferLength / halfCount);
          for (let o = 0; o < halfCount; o++) {
            const start = o * bucketSize;
            const end = start + bucketSize;
            let sum = 0;
            for (let j = start; j < end; j++) sum += dataArray[j];
            const avg = sum / bucketSize; // 0..255
            const normalized = avg / 255; // 0..1
            const height = Math.round(minHeight + normalized * range);

            // Índices simétricos desde el centro hacia los extremos
            const leftIndex = halfCount - 1 - o;
            const rightIndex = halfCount + o;

            const leftSpan = wavesRef.current.children[leftIndex];
            const rightSpan = wavesRef.current.children[rightIndex];
            if (leftSpan) leftSpan.style.height = `${height}px`;
            if (rightSpan) rightSpan.style.height = `${height}px`;
          }
          rafRef.current = requestAnimationFrame(updateBars);
        };

        setIsLive(true);
        rafRef.current = requestAnimationFrame(updateBars);
      } catch {
        // Fallback to CSS animation if user denies or not available
        setIsLive(false);
      }
    }

    setupAnalyser();

    return () => {
      isMounted = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
      if (sourceRef.current) sourceRef.current.disconnect();
      if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <div className="chat-audio-recorder" role="region" aria-label={t('Recording...')}>
      <div className="chat-audio-recorder__content">
        <div className={`chat-audio-recorder__waves ${isLive ? 'chat-audio-recorder__waves--live' : ''}`} aria-hidden="true" ref={wavesRef}>
          {Array.from({ length: 24 }).map((_, i) => (
            <span key={i} />
          ))}
        </div>
        <div className="chat-audio-recorder__actions">
          <button type="button" className="chat-audio-recorder__icon-btn chat-audio-recorder__icon-btn--white" onClick={onCancel} aria-label={t('Cancel')}>
            <Icon type="close" color="black" />
          </button>
          <button
            type="button"
            className="chat-audio-recorder__icon-btn chat-audio-recorder__icon-btn--white"
            onClick={onStop}
            aria-label={t('Stop recording')}
          >
            <Icon type="check" color="black" />
          </button>
        </div>
      </div>
    </div>
  );
}

ChatAudioRecorder.propTypes = {
  onCancel: PropTypes.func,
  onStop: PropTypes.func,
};

ChatAudioRecorder.defaultProps = {
  onCancel: function () {},
  onStop: function () {},
};

export default ChatAudioRecorder;
